import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {useNavigate } from 'react-router-dom';
import PropertyModal from '../../Components/PropertyCard/PropertyModal';
import { MapPin, Home, Building, BedDouble, Filter, X, Heart, TrendingUp, Clock } from 'lucide-react';
import api from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../utils/avatarColor';



// Custom Currency Selector Component (same as HomeDash)
function CurrencySelector({ userCurrency, setUserCurrency, fxRates, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(userCurrency);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedCurrency(userCurrency);
  }, [userCurrency]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (currency) => {
    setSelectedCurrency(currency);
    setUserCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-[var(--bg-black)] text-[var(--text-black)] border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[80px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCurrency || 'Currency'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {Object.keys(fxRates).map((currency) => (
            <div
              key={currency}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedCurrency === currency ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(currency)}
            >
              {currency}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Custom Language Selector Component (same as HomeDash)
function LanguageSelector({ i18n, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const dropdownRef = useRef(null);
  const languages = [
    { code: "kn", name: "Kinyarwanda" },
    { code: "sw", name: "Swahili" },
    { code: "en", name: "English" },
    { code: "lg", name: "Luganda" },
    { code: "zu", name: "Zulu" },
    { code: "fr", name: "Français" },
    { code: "ar", name: "العربية" },
    { code: "hi", name: "हिन्दी" },
    { code: "zh-Hant", name: "中文 (Traditional)" },
    { code: "zh-Hans", name: "中文 (Simplified)" },
    { code: "ak", name: "Akan" },
    { code: "am", name: "Amharic" },
    { code: "ay", name: "Aymara" },
    { code: "bm", name: "Bambara" },
    { code: "be", name: "Bemba" },
    { code: "bn", name: "Bengali" },
    { code: "ber", name: "Berber" },
    { code: "nl", name: "Dutch" },
    { code: "de", name: "German" }, 
    { code: "Gik", name: "Gikuyu" },
    { code: "gr", name: "Greek" }, 
    { code: "es", name: "Spanish" },
    { code: "fi", name: "Finnish" },
    { code: "ha", name: "Hausa" },
    { code: "ig", name: "Igbo" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "km", name: "Khmer" },
    { code: "ki", name: "Kikuyu" },
    { code: "ko", name: "Korean" },
    { code: "ht", name: "Haitian Creole" },
    { code: "mg", name: "Malagasy" },
    { code: "ml", name: "Malayalam" },
    { code: "mr", name: "Marathi" },
    { code: "ne", name: "Nepali" },
    { code: "om", name: "Oromo" },
    { code: "ps", name: "Pashto" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "qu", name: "Quechua" },
    { code: "ru", name: "Russian" },
    { code: "so", name: "Somali" },
    { code: "sq", name: "Albanian" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "ti", name: "Tigrinya" },
    { code: "tr", name: "Turkish" },
    { code: "ur", name: "Urdu" },
    { code: "vi", name: "Vietnamese" },
    { code: "wo", name: "Wolof" },
    { code: "xh", name: "Xhosa" },
    { code: "yo", name: "Yoruba" },
    { code: "zu", name: "Zulu" }
  ];

  const selectedLanguage = languages.find(lang => lang.code === selectedLang);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-pink-500 text-white border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[100px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLanguage?.name || 'Language'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedLang === lang.code ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(lang.code)}
            >
              {lang.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export default function ExploreDash({ setShowLoginModal }) {
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCell, setSelectedCell] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());

  const observerTarget = useRef(null);
  const [visibleCount, setVisibleCount] = useState(6);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sortRef = useRef(null);
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({});

  const {t, i18n} = useTranslation('Exploredash');

  const [authReady, setAuthReady] = useState(false);

  const sortOptions = useMemo(() => [
    { value: 'recent', label: t('explore.filters.sortOptions.recent') },
    { value: 'popular', label: t('explore.filters.sortOptions.popular') },
    { value: 'price-low', label: t('explore.filters.sortOptions.priceLow') },
    { value: 'price-high', label: t('explore.filters.sortOptions.priceHigh') },
  ], [t]);


  useEffect(() => {
    const fetchGlobalData = async () => {
      const fxRes = await api.get('/fx-rates/');
      setFxRates(fxRes.data);
      const savedCurrency = localStorage.getItem('preferredCurrency') || 'RWF';
      setUserCurrency(savedCurrency);
    };
    fetchGlobalData();
  }, []);

  useEffect(() => {
  // Wait for CSRF token to be fetched before enabling actions
  const initAuth = async () => {
    try {
      await import('../../api/axiosConfig').then(({ getCSRFToken }) => 
        getCSRFToken()
      );
      setAuthReady(true);
    } catch (err) {
      console.warn('Auth init failed:', err);
      setAuthReady(true); // Still allow UI, actions may fail gracefully
    }
  };
  initAuth();
}, []);



useEffect(() => {
    const hasSeenLangPrompt = localStorage.getItem('pongo_lang_prompt');
    if (!hasSeenLangPrompt) {
        let browserLang = navigator.language.split('-')[0];

        if (browserLang === 'zh') {
          browserLang = 'zh-Hans';
        }

        const supportedLangs = [
          'en', 'fr', 'es', 'pt', 'ar', 'sw', 'kn', 'hi', 'ru', 'de', 'ja',
          'ak', 'am', 'ay', 'bm', 'be', 'bn', 'ber', 'zh-Hans', 'zh-Hant', 'nl', 'fi', 'ha', 
          'ig', 'id', 'it', 'km', 'ki', 'ko', 'ht', 'lg', 'mg', 'ml', 
          'mr', 'ne', 'om', 'ps', 'pl', 'pa', 'qu', 'ru', 'so', 'sq', 'sw', 
          'ta', 'te', 'th', 'ti', 'tr', 'ur', 'vi', 'wo', 'xh', 'yo', 'zu'
        ];


        if (supportedLangs.includes(browserLang)) {
          const langNameMap = {
            'kn': 'Kinyarwanda',
            'zh-Hans': 'Simplified Chinese',
            'zh-Hant': 'Traditional Chinese',
            // ... others if needed
          };
          const langDisplayName = langNameMap[browserLang] || browserLang.toUpperCase();
          
          const userChoice = window.confirm(`Switch to ${langDisplayName}?`);
          if (userChoice) {
            i18n.changeLanguage(browserLang);
            localStorage.setItem('language', browserLang);
          }
        }
        localStorage.setItem('pongo_lang_prompt', 'true');
    }
}, [i18n]);


const convertPrice = useCallback((amount, fromCurrency) => {
  if (fromCurrency === userCurrency) return amount;
  const fromRate = fxRates[fromCurrency] || 1;
  const toRate = fxRates[userCurrency] || 1;
  return (amount / fromRate) * toRate;
}, [userCurrency, fxRates]);


const formatPrice = useCallback((price, currency) => {
  const converted = convertPrice(price, currency);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: userCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(converted);
}, [userCurrency, convertPrice]); 


// Auto-detect from browser
const getBrowserCurrency = () => {
  try {
    return new Intl.NumberFormat().resolvedOptions().currency;
  } catch {
    return 'USD'; 
  }
};

useEffect(() => {
  const fetchGlobalData = async () => {
    try {
      const fxRes = await api.get('/fx-rates/');
      setFxRates(fxRes.data);

      const saved = localStorage.getItem('preferredCurrency');
      const browserCurrency = getBrowserCurrency();
      const defaultCurrency = saved || browserCurrency || 'USD';
      const finalCurrency = fxRes.data[defaultCurrency] ? defaultCurrency : 'USD';
      
      setUserCurrency(finalCurrency);
    } catch (err) {
      console.error('Failed to fetch global data:', err);
      setUserCurrency('USD');
    }
  };
  fetchGlobalData();
}, []);



  // CHANGED: Use the unique-users endpoint to get one post per user
  useEffect(() => {
    let isCancelled = false;
    
    const fetchListings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/properties/unique-users/');
        const data = response.data.results || response.data;
        if (!isCancelled) {
          setListings(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load properties:', err);
        if (!isCancelled) {
          setError('Failed to load properties. Please try again later.');
          setListings([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    fetchListings();
    
    return () => {
      isCancelled = true;
    };
  }, []);
  

  const handlePropertyClick = async (property) => {
    try {
      const res = await api.get(`/properties/${property.id}/`);
      setSelectedProperty(res.data);
      
      setListings(prev => 
        prev.map(p => p.id === property.id ? res.data : p)
      );
    } catch (err) {
      console.error('Failed to fetch full property:', err);
      setSelectedProperty(property);
    }
    setIsModalOpen(true);
  };


  const handleMessageOwner = (property, conversation) => {
    if (!property || !property.id || !conversation || !conversation.id) {
      console.warn('Invalid property/conversation passed to handleMessageOwner');
      return;
    }

    console.log("Conversation created/fetched:", conversation);
    navigate(`/messages/${conversation.id}`);
  };

  // Filter and sort listings - UPDATED for unique users
  const filteredListings = useMemo(() => {
    let filtered = listings.filter(listing => {
      const matchesType = selectedType === 'all' || listing.type === selectedType;
      const matchesCity = selectedCity === 'all' || listing.city === selectedCity;
      const matchesCell = selectedCell === 'all' || listing.sector === selectedCell;
      return matchesType && matchesCity && matchesCell;
    });

    if (sortBy === 'recent') {
      // Sort by posted_at (newest first). Fallback to id if needed.
      filtered.sort((a, b) => {
        const dateA = new Date(a.posted_at || 0);
        const dateB = new Date(b.posted_at || 0);
        return dateB - dateA; // descending: newest first
      });
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => (b.likes_count || b.likes || 0) - (a.likes_count || a.likes || 0));
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price_rwf - b.price_rwf);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price_rwf - a.price_rwf);
    }

    return filtered;
  }, [selectedType, selectedCity, selectedCell, sortBy, listings]);

  const activeFiltersCount = [selectedType, selectedCity, selectedCell].filter(f => f !== 'all').length;
  

  const formatNumber = (num) => {
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(1)}B`;
    }
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`.replace(/\.0$/, ''); 
    }
    return num.toString();
  };

  const toggleLike = (id) => {
    setLikedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Infinite scroll
  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + 5, filteredListings.length));
  }, [filteredListings.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );
    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore]);

  const clearAllFilters = () => {
    setSelectedType('all');
    setSelectedCity('all');
    setSelectedCell('all');
  };

  const modalPropertyIndex = useMemo(() => {
    if (!selectedProperty?.id) return 0;
    const idx = filteredListings.findIndex(p => String(p.id) === String(selectedProperty.id));
    return idx >= 0 ? idx : 0; // fallback to 0 if not found
  }, [selectedProperty, filteredListings]);

  //dropdown when clicking outside
  useEffect(() => {
  const handleClickOutside = (e) => {
    if (isSortOpen && sortRef.current && !sortRef.current.contains(e.target)) {
      setIsSortOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [isSortOpen]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg-black)]">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-20 bg-[var(--bg-black)] text-white">
        <p className="text-lg">{t('explore.errors.loadFailed')}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-pink-500 border-none text-white rounded-full"
        >
          {t('explore.errors.retry')}
        </button>
      </div>
    );
  }



  return (
    <div className={`min-h-screen transition-colors duration-300 `}>
      
      {/* Top Header - Sticky */}
      <header className="sticky top-0 z-30 bg-[var(--bg-black)] border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Use flex-wrap + adaptive height */}
          <div className="flex flex-wrap items-center justify-between gap-2 py-2 min-h-[64px]">
            
            {/* LEFT: Logo + Labels (constrained width) */}
            <div className="flex flex-wrap items-center gap-2 max-w-full">
              <MapPin className="text-pink-500 flex-shrink-0" size={24} />

              {/* "Get Home" pill: scrollable text on small screens */}
              <div className="max-w-[140px] xs:max-w-[160px] sm:max-w-none overflow-x-auto whitespace-nowrap no-scrollbar">
                <h1 className="text-xs xs:text-sm text-[var(--text-black)] bg-white/10 rounded-full px-3 py-1 inline-block">
                  {t('explore.header.getHome')}
                </h1>
              </div>

              <CurrencySelector 
                userCurrency={userCurrency} 
                setUserCurrency={setUserCurrency} 
                fxRates={fxRates} 
                localStorage={localStorage} 
              />
              <LanguageSelector 
                i18n={i18n} 
                localStorage={localStorage} 
              />
            </div>

            {/* RIGHT: Filter button (won’t push layout) */}
            <div className="mt-1 sm:mt-0 flex-shrink-0">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 border-none bg-pink-500 text-white rounded-full text-xs font-medium whitespace-nowrap"
              >
                <Filter size={14} />
                <span className="hidden xs:inline">{t('explore.filters.button')}</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-white text-pink-500 text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
            
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Filters Sidebar - Desktop & Mobile Overlay */}
          <aside 
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowMobileFilters(false);
              }
            }}
            className={`${showMobileFilters ? 'fixed inset-0 z-50 bg-black/50' : 'hidden'}`}
          >
            <div
              className={`
                fixed right-0 top-0 bottom-0 w-80 max-w-full
                bg-[var(--bg-black)] rounded-l-xl shadow-lg
                flex flex-col
                transform transition-transform duration-300 ease-in-out
                ${showMobileFilters ? 'translate-x-0' : 'translate-x-full'} z-50
              `}
            >
              {/* Header – fixed, non-scrolling */}
              <div className="flex items-center justify-between p-4 border-b border-white/40 bg-[var(--bg-black)]">
                <h2 className="text-lg font-bold text-[var(--text-black)]">
                  {t('explore.filters.title')}
                </h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-pink-500 text-sm bg-[var(--bg-black)] border-none font-bold"
                  aria-label="Close filters"
                >
                  ✕
                </button>
              </div>

              {/* Scrollable filter content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Sort By */}
                <div className="relative" ref={sortRef}>
                  <label className="block text-sm font-semibold text-[var(--text-black)] mb-2">
                    {t('explore.filters.sortBy')}
                  </label>
                  <button
                    onClick={() => setIsSortOpen(!isSortOpen)}
                    className="w-full px-4 py-2.5 rounded-lg border border-white/40 bg-[var(--bg-black)] text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm flex justify-between items-center"
                  >
                    {sortOptions.find(opt => opt.value === sortBy)?.label || 'Sort By'}
                    <svg
                      className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isSortOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--bg-black)] border border-white/40 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortOpen(false);
                          }}
                          className={`w-full text-left bg-[var(--bg-black)] text-[var(--text-black)] border-none px-4 py-2.5 text-sm font-medium transition ${
                            sortBy === option.value
                              ? 'bg-pink-500 text-white'
                              : 'text-[var(--text-black)] hover:bg-white/10'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Property Type */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--text-black)] mb-3">
                    {t('explore.filters.propertyType')}
                  </label>
                  <div className="space-y-2">
                    {[
                      { id: 'all', label: t('explore.filters.categories.all'), icon: MapPin },
                      { id: 'rental', label: t('explore.filters.categories.rental'), icon: Home },
                      { id: 'property', label: t('explore.filters.categories.property'), icon: Building },
                      { id: 'hotel', label: t('explore.filters.categories.hotel'), icon: BedDouble }
                    ].map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => setSelectedType(filter.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${
                          selectedType === filter.id
                            ? 'bg-pink-500 text-white shadow-md border border-white/40'
                            : 'bg-[var(--bg-black)] text-[var(--text-black)] border border-white/40'
                        }`}
                      >
                        <filter.icon size={18} />
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="w-full px-4 py-3 bg-pink-500 text-white border-none rounded-lg font-medium transition shadow-md flex items-center justify-center gap-2"
                  >
                    <X size={18} />
                    Clear All Filters ({activeFiltersCount})
                  </button>
                )}
              </div>
            </div>
          </aside>
          
          <main className="flex-1 min-w-0">
            <div className="mb-6 p-4 bg-[var(--bg-black)] border border-white/40 border-solid rounded-xl shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="text-pink-500" size={20} />
                    <span className="text-sm font-medium text-[var(--text-black)]">
                      {t('explore.results.propertiesFound', { count: filteredListings.length })}
                    </span>
                  </div>
                  {activeFiltersCount > 0 && (
                    <div className="flex items-center gap-2 ">
                      <Filter className="text-blue-500" size={18} />
                      <span className="text-sm bg-[var(--bg-black)] text-[var(--text-black)] border-none">
                        {t('explore.results.activeFilters', { count: activeFiltersCount })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Property Cards Feed */}
            {filteredListings.length === 0 ? (
              <div className="text-center py-20 bg-[var(--bg-black)] rounded-xl shadow-sm">
                <MapPin size={64} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {t('explore.results.noProperties')}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {t('explore.results.adjustFilters')}
                </p>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="px-6 py-2 bg-pink-500 text-white border-none rounded-full font-medium hover:bg-pink-600 transition"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Card styling */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredListings.slice(0, visibleCount).map((listing) => (
                    <article 
                      key={listing.id}
                      property={listing}
                      className="bg-[var(--bg-black)] rounded-xl property-card-shadow overflow-hidden rounded-xl shadow-md  hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                      onClick={() => handlePropertyClick(listing)}
                    >
                      {/* Property Image */}
                      <div className="relative bg-[var(--bg-black)]">
                        <img 
                          src={listing.images?.length > 0 ? listing.images[0].image : '/fallback.jpg'} 
                          alt={listing.title}
                          className="w-full h-48 object-cover rounded-t-lg"
                          loading="lazy"
                        />

                        {/* Type Badge */}
                        <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-[var(--text-black)] shadow-lg ${
                          listing.type === 'rental' ? 'bg-[var(--bg-black)]' :
                          listing.type === 'property' ? 'bg-[var(--bg-black)]' :
                          'bg-purple-500'
                        }`}>
                          {listing.type === 'rental' ? t('explore.property.types.rental'):
                          listing.type === 'property' ? t('explore.property.types.property') :
                          t('explore.property.types.hotel')}
                        </span>

                        {/* Price Badge */}
                        <span className="absolute top-3 left-32 px-3 py-1 rounded-lg text-sm font-bold text-[var(--text-black)] bg-[var(--bg-black)] backdrop-blur-sm">
                          {formatPrice(listing.price_rwf, listing.price_currency)}
                        </span>

                        {/* Like Button */}
                        <div 
                          className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2.5 py-1.5 rounded-full text-white cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(listing.id);
                          }}
                        >
                          <Heart
                            size={16}
                            className={`${
                              likedPosts.has(listing.id)
                                ? 'fill-pink-500 text-pink-500 sticky'
                                : 'text-white sticky'
                            }`}
                          />
                          <span className="text-xs font-medium sticky">
                            {formatNumber(listing.likes_count + (likedPosts.has(listing.id) ? 1 : 0))}
                          </span>
                        </div>
                      </div>

                      {/* User Header */}
                      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          {(() => {
                            const displayName = listing.owner?.username || 'U';
                            const initials = displayName.charAt(0).toUpperCase();
                            // Prefer real avatar if available and valid
                            const hasRealAvatar = listing.owner?.avatar && String(listing.owner.avatar).startsWith('http');
                            const avatarUrl = hasRealAvatar 
                              ? listing.owner.avatar 
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${stringToColor(displayName || 'user').substring(1)}&color=fff`;

                            return (
                              <img
                                src={avatarUrl}
                                alt={listing.owner?.username || t('explore.property.unknownUser')}
                                className="w-10 h-10 rounded-full ring-2 ring-pink-500"
                                onError={(e) => {
                                  // Fallback to generated avatar if real one fails
                                  if (hasRealAvatar) {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${stringToColor(displayName || 'user').substring(1)}&color=fff`;
                                  }
                                }}
                              />
                            );
                          })()}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                {listing.owner?.username || t('explore.property.posted')}
                              </h3>
                              {listing.owner?.verified && (
                                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                </svg>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Clock size={12} />
                              {listing.posted_at || 'Just now'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Property Details */}
                      <div className="p-4">                        
                        <div className="flex items-center border border-none gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                          <div className="flex items-center gap-1">
                            <MapPin size={16} className="text-pink-500" />
                            <span className='text-[var(--text-black)]'>{listing.location}</span>
                          </div>
                          {listing.bedrooms > 0 && (
                            <div className="flex items-center gap-1 text-[var(--text-black)]">
                              <BedDouble size={16} />
                              <span>{listing.bedrooms} Bed</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                
                {/* Load More Trigger */}
                {visibleCount < filteredListings.length && (
                  <div ref={observerTarget} className="py-8 text-center">
                    <div className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading more...</p>
                  </div>
                )}

                {/* End of Results */}
                {visibleCount >= filteredListings.length && filteredListings.length > 0 && (
                  <div className="py-8 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('explore.results.endReached')}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {t('explore.results.propertiesShown', { count: filteredListings.length })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      <PropertyModal
        properties={filteredListings}
        property={selectedProperty}
        initialIndex={modalPropertyIndex}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onMessageOwner={handleMessageOwner}
        actionsDisabled={!authReady}
      />
    </div>
  );
}